import { useState, useCallback } from "react";
import { User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { trackFunnelEvent } from "@/services/FunnelAnalytics";
import { createGenerationResultId } from "@/services/PostGenerationSupportCtaAnalytics";
import {
  CustomerGenerationError,
  getGenerationFailureAnalytics,
  getSafeGenerationErrorMessage,
  parseFunctionFailure,
  shouldRetryGeneration,
  type GenerationFailurePhase,
} from "@/services/generation-error";

interface GenerationResponse {
  success: boolean;
  imageUrl?: string;
  creditsRemaining?: number;
  creditsRefunded?: boolean;
  error?: string;
}

// Keep retries bounded so a provider failure cannot consume the Edge Function budget indefinitely.
const MAX_RETRIES = 1;
const BASE_DELAY_MS = 2000;

export const useImageGeneration = (user: User | null) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedImage, setGeneratedImage] = useState<string | null>(null);
  const [generatedResultId, setGeneratedResultId] = useState<string | null>(null);
  const [generationDebug, setGenerationDebug] = useState<Record<string, unknown> | null>(null);
  const { toast } = useToast();

  const updateGeneratedImage = useCallback((
    image: string | null,
    trackGenerationSucceeded?: () => boolean | void | Promise<boolean | void>,
  ) => {
    const resultId = image ? createGenerationResultId(trackGenerationSucceeded) : null;
    setGeneratedImage(image);
    setGeneratedResultId(resultId);
  }, []);

  const generateImage = useCallback(async (
    selectedImage: File | null,
    selectedStyle: string,
    _imagePreview: string | null,
    selectedSize: string,
    aspectRatio?: string,
    subStyle?: string | null,
  ) => {
    if (!selectedImage || !selectedStyle || !user) {
      toast({
        title: "Informations manquantes",
        description: "Veuillez sélectionner une image et un style",
        variant: "destructive",
      });
      return false;
    }

    setIsGenerating(true);
    setGenerationDebug(null);

    const startTime = Date.now();
    let attempts = 0;
    let failurePhase: GenerationFailurePhase = "image_decode";

    try {
      // Convert image file to base64 with EXIF orientation fix + resize
      const imageBitmap = await createImageBitmap(selectedImage);

      const MAX_DIM = 1500;
      let { width, height } = imageBitmap;
      if (width > MAX_DIM || height > MAX_DIM) {
        const scale = MAX_DIM / Math.max(width, height);
        width = Math.round(width * scale);
        height = Math.round(height * scale);
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Canvas context unavailable');
      ctx.drawImage(imageBitmap, 0, 0, width, height);
      imageBitmap.close();

      const imageDataUrl = canvas.toDataURL('image/jpeg', 0.85);
      const base64String = imageDataUrl.split(',')[1];

      const requestBody = {
        imageBase64: base64String,
        style: selectedStyle,
        subStyle: subStyle || undefined,
        formatSize: selectedSize || 'portrait_30x40',
        aspectRatio: aspectRatio,
      };
      failurePhase = "edge_function";

      // Invoke edge function with retry for transient errors
      let responseData: GenerationResponse | null = null;

      trackFunnelEvent("generation_started", {
        style: selectedStyle,
        subStyle: subStyle || null,
        formatSize: selectedSize || 'portrait_30x40',
        aspectRatio: aspectRatio || null,
        attempt: 1,
      });

      for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
        attempts = attempt + 1;
        if (attempt > 0) {
          const delay = BASE_DELAY_MS * Math.pow(2, attempt - 1) + Math.random() * 1000;
          console.log(`[generation] Retry ${attempt}/${MAX_RETRIES} in ${Math.round(delay)}ms...`);
          await new Promise(r => setTimeout(r, delay));
        }

        const { data, error } = await supabase.functions.invoke<GenerationResponse>('generate-artwork', {
          body: requestBody,
        });

        if (!error && data?.success && data.imageUrl) {
          responseData = data;
          break;
        }

        const failure = error ? await parseFunctionFailure(error) : null;
        // Transport failures have an ambiguous server outcome and must not be
        // retried without a server-side idempotency key. Only retry when the
        // function explicitly confirms both retryability and credit refund.
        const retryable = shouldRetryGeneration(failure);

        if (!retryable) {
          throw new CustomerGenerationError(failure, failurePhase);
        }

        if (attempt === MAX_RETRIES) {
          throw new CustomerGenerationError(failure, failurePhase);
        }
      }

      if (!responseData?.imageUrl) {
        throw new Error('Aucune URL d\'image retournée');
      }

      setGenerationDebug({ source: 'generation-service' });

      const totalSeconds = Math.round((Date.now() - startTime) / 1000);
      const creditsRemaining = responseData.creditsRemaining;
      updateGeneratedImage(responseData.imageUrl, () => trackFunnelEvent("generation_succeeded", {
        style: selectedStyle,
        subStyle: subStyle || null,
        formatSize: selectedSize || 'portrait_30x40',
        aspectRatio: aspectRatio || null,
        attempts,
        durationSeconds: totalSeconds,
        creditsRemaining,
      }));
      toast({
        title: "Image générée !",
        description: `Votre artwork a été créé en ${totalSeconds}s — ${creditsRemaining} crédit${creditsRemaining !== 1 ? 's' : ''} restant${creditsRemaining !== 1 ? 's' : ''}`,
      });

      return true;

    } catch (error) {
      console.error('Generation error:', error);
      const failureAnalytics = getGenerationFailureAnalytics(error, failurePhase);
      trackFunnelEvent("generation_failed", {
        style: selectedStyle,
        subStyle: subStyle || null,
        formatSize: selectedSize || 'portrait_30x40',
        aspectRatio: aspectRatio || null,
        attempts: Math.max(attempts, 1),
        durationSeconds: Math.round((Date.now() - startTime) / 1000),
        ...failureAnalytics,
      });

      const errorMessage = getSafeGenerationErrorMessage(error);

      toast({
        title: "Erreur de génération",
        description: errorMessage,
        variant: "destructive",
      });
      return false;
    } finally {
      setIsGenerating(false);
    }
  }, [user, toast, updateGeneratedImage]);

  const resetGeneration = useCallback(() => {
    updateGeneratedImage(null);
    setIsGenerating(false);
    setGenerationDebug(null);
  }, [updateGeneratedImage]);

  return {
    isGenerating,
    generatedImage,
    generatedResultId,
    generationDebug,
    generateImage,
    resetGeneration,
    setGeneratedImage: updateGeneratedImage, // For resume functionality
  };
};
